import {unwrapResult} from '@reduxjs/toolkit';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Col, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {BIRTH_CONTROL_REFILL_KEYS, METHOD_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire, bookAppointment} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';
import {redirectInIframe} from '~/utils/helpers';

const Questionnaire5Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage} = useNotification(dispatch);
  const [isShowSorryModal, setIsShowSorryModal] = useState(false);

  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);

  const {loading} = useRequestState(addQuestionnaire);
  const {loading: loadingAppointment} = useRequestState(bookAppointment);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REFILL_KEYS.STATEMENT_BEST_APPLIES]?.data,
  );
  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REFILL_KEYS.STATEMENT_BEST_APPLIES);
  const {inputs, setInputs} = useInput({selectedOption: currentAnswer?.selectedOption || ''});

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({selectedOption: value});
  };

  const onClickSubmit = async () => {
    const questionnaireAnswer = {
      key: BIRTH_CONTROL_REFILL_KEYS.STATEMENT_BEST_APPLIES,
      index: questionnaire?.index || '',
      data: inputs,
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    if (inputs.selectedOption === 3 || inputs.selectedOption === 4) {
      const resAction = await dispatch(
        addQuestionnaire({
          data: {
            data: {
              ...medicalQuestionnaireAnswer,
              [BIRTH_CONTROL_REFILL_KEYS.STATEMENT_BEST_APPLIES]: {...questionnaireAnswer},
            },
            wpUserId: wpAuthUser?.wpUserId,
            methodKey: METHOD_KEYS.BIRTH_CONTROL_REFILL,
          },
        }),
      );
      if (requestOK(resAction, addQuestionnaire)) {
        const result = unwrapResult(resAction);
        if (result?.booking) {
          const type = result?.slotTime;
          dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
          history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.BIRTH_CONTROL_REFILL}&token=${savedToken}`);
        } else if (result?.booking === false) {
          const appointmentAction = await dispatch(
            bookAppointment({
              data: {startTime: null, userQuestionnaireId: result?.medical?.id},
              platform: isWebview ? 1 : 2,
            }),
          );
          if (requestOK(appointmentAction, bookAppointment)) {
            const bookResult = unwrapResult(appointmentAction);
            if (bookResult?.checkoutURL) {
              redirectInIframe(bookResult.checkoutURL, '_self');
            }
          }
        }
      }
    } else {
      dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REFILL_KEYS.MEDICAL_HISTORY}));
    }
  };

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        handleOnNext={onClickSubmit}
        disableNext={!inputs.selectedOption}
        actions={questionnaire?.actions}
        loading={loading || loadingAppointment}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiBox mt={spacing.medium} w="100%">
            <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
              {questionnaire?.options?.map(value => (
                <ScrollToBottomButton
                  key={`${value.label}-${value.value}`}
                  title={value.label}
                  variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                  onClick={() => onClickOption(value)}
                  height="64px"
                />
              ))}
            </UiSimpleGrid>
          </UiBox>
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire5Screen;
